import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import * as React from 'react';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab, Typography, Box, CircularProgress } from '@mui/material';
import { SiteStatus } from '../sections/siteStatus';
import { fetchBessStatusBySystemId } from '../redux/slices/bessSlice';


// ========== TabPanel Component =========

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="span">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};


export default function SiteStatusPage({ msSessionInfo, allSites }) {
  // ========== Initialization ==========
  const { systemIds } = useParams();
  const [value, setValue] = useState(0);
  // const [completed, setCompleted] = useState(false);
  const [completedSystemIds, setCompletedSystemIds] = useState(new Set());
  const bessData = useSelector((state) => state.bess.data);


  const dispatch = useDispatch();

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Set the initial tab based on the systemIds from the URL
  useEffect(() => {
    // If length of system IDs is 0, default to the first tab
    if (allSites?.length && systemIds) {
      const matchIndex = allSites.findIndex(site => site.id === systemIds);
      if (matchIndex !== -1) {
        setValue(matchIndex);
      }
    }
  }, [allSites, systemIds]);

  // ========= Fetch BESS data when the selected site changes =========
  // Run this effect when the selected tab changes or when allSites or msSessionInfo changes
  // This will ensure that we fetch the BESS data for the currently selected site
  // and only fetch it once per site.
  // We also check if the data for the current site has already been fetched to avoid duplicate requests.
  useEffect(() => {
    if (allSites?.[value] && msSessionInfo) {
      const currentSystemId = allSites[value].id;

      const alreadyFetched = bessData?.some(b => b.systemId === currentSystemId);
      if (alreadyFetched) return;

      dispatch(fetchBessStatusBySystemId({ msSessionInfo, systemIds: [currentSystemId] }))
        .finally(() => {
          setCompletedSystemIds(prev => new Set(prev).add(currentSystemId));
        });

    }
  }, [value, allSites, msSessionInfo, bessData, dispatch]);



  return (
    <>
      <Helmet>
        <title>SYSO System Status</title>
      </Helmet>

      <Box sx={{ pl: 5, pr: 5 }}>
        <Typography variant="h4" sx={{ pb: 1 }}>System Status</Typography>
        { /* If no sites are available, show a loading spinner */}
        {!allSites || allSites.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="SiteStatus"
                variant="scrollable"
                scrollButtons="auto"
              >
                {allSites.map((site) => (
                  <Tab key={site.id} label={site.name} />
                ))}
              </Tabs>
            </Box>

            {allSites.map((site, index) => {
              const matchedBessEntry = bessData?.find(b => b.systemId === site.id);
              const isCompletedForThisSystem = completedSystemIds.has(site.id);
              return (
                <TabPanel key={site.id} value={value} index={index}>
                  {matchedBessEntry ? (
                    <SiteStatus
                      msSessionInfo={msSessionInfo}
                      site={site}
                      bessData={matchedBessEntry}
                    />
                  )
                    : isCompletedForThisSystem ? (
                      <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                        No data available for {site.name}.
                      </Typography>
                    )
                      : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <CircularProgress />
                        </Box>
                      )}
                </TabPanel>
              );
            })}
          </>
        )}
      </Box>
    </>
  );
}
